import { z } from "zod";
import { ContentFitnessVerificationStateSchema } from "../../enums/content-fitness";
import {
  EVAL_JUDGE_KIND,
  EvalJudgePassOrderSchema,
  EvalWinnerSchema,
} from "../../enums/evals";
import { CheckJudgmentSchema, FindingSeveritySchema } from "../../enums/yeet";

/**
 * The judge worker's two backend calls for one pairwise comparison:
 *
 *   GET  /internal/evals/comparisons/:comparisonId/briefing  -> briefing
 *   POST /internal/evals/comparisons/:comparisonId/judgment  -> 201
 *
 * The briefing is everything the judge is allowed to see. `agentModel` and
 * `promptBundleLabel` identify the variant for the WRITER of the judgment, not
 * for the judge: the prompt renders only the output, under neutral position
 * labels, so the model cannot vote for a brand it recognises.
 */

const EvalFindingBriefingSchema = z.object({
  metricKey: z.string().min(1),
  severity: FindingSeveritySchema,
  title: z.string(),
  detail: z.string(),
  recommendedAction: z.string().nullable(),
});

const EvalCheckJudgmentBriefingSchema = z.object({
  metricKey: z.string().min(1),
  checkKey: z.string(),
  judgment: CheckJudgmentSchema,
  points: z.number().int(),
  rationale: z.string().nullable(),
});

const EvalAssessmentBriefingSchema = z.object({
  subcriterionKey: z.string(),
  parentMetricKey: z.string(),
  // Null on the explicit absence states (`not_applicable`,
  // `unable_to_verify`); never a stand-in zero.
  score: z.number().int().nullable(),
  verificationState: ContentFitnessVerificationStateSchema,
  rationale: z.string(),
});

const EvalSignalBriefingSchema = z.object({
  metricKey: z.string().min(1),
  score: z.number().int(),
  detail: z.string().nullable(),
});

/** One variant's whole analysis output, as the judge sees it. */
export const EvalVariantOutputSchema = z.object({
  summary: z.string().nullable(),
  findings: z.array(EvalFindingBriefingSchema),
  checkJudgments: z.array(EvalCheckJudgmentBriefingSchema),
  assessments: z.array(EvalAssessmentBriefingSchema),
  signals: z.array(EvalSignalBriefingSchema),
});
export type EvalVariantOutput = z.infer<typeof EvalVariantOutputSchema>;

export const EvalVariantBriefingSchema = z.object({
  label: z.string(),
  agentModel: z.string(),
  promptBundleLabel: z.string().nullable(),
  output: EvalVariantOutputSchema,
});
export type EvalVariantBriefing = z.infer<typeof EvalVariantBriefingSchema>;

export const EvalJudgeBriefingResponseSchema = z.object({
  comparisonId: z.uuid(),
  websiteId: z.uuid(),
  url: z.string(),
  variants: z.object({
    a: EvalVariantBriefingSchema,
    b: EvalVariantBriefingSchema,
  }),
});
export type EvalJudgeBriefingResponse = z.infer<
  typeof EvalJudgeBriefingResponseSchema
>;

export const EvalJudgmentPerMetricSchema = z.object({
  metricKey: z.string().min(1),
  winner: EvalWinnerSchema,
  note: z.string(),
});
export type EvalJudgmentPerMetric = z.infer<typeof EvalJudgmentPerMetricSchema>;

/** One presentation order and the winner it produced, mapped back to a/b. */
export const EvalJudgmentPassSchema = z.object({
  order: EvalJudgePassOrderSchema,
  winner: EvalWinnerSchema,
});
export type EvalJudgmentPass = z.infer<typeof EvalJudgmentPassSchema>;

/**
 * The judgment the worker posts back. `passes` is the audit trail of the
 * position-bias control: both presentation orders with the winner each one
 * produced, already mapped back to variant a/b. A `winner` of `tie` where the
 * two passes disagree is a positional disagreement, and `rationale` says so.
 */
export const EvalJudgmentWriteRequestSchema = z
  .object({
    judgeKind: z.literal(EVAL_JUDGE_KIND.LLM),
    winner: EvalWinnerSchema,
    rationale: z.string().min(1),
    perMetric: z.array(EvalJudgmentPerMetricSchema),
    judgeModel: z.string().min(1),
    passes: z.array(EvalJudgmentPassSchema).length(2),
  })
  .strict();
export type EvalJudgmentWriteRequest = z.infer<
  typeof EvalJudgmentWriteRequestSchema
>;
